import { CHANNELS } from "../../shared/channels";
import { BudgetDraft, BudgetItem, BudgetKind, BudgetRepeat, BudgetState } from "../../shared/types";
import { fixedByCategory, formatMoney, lastMonths, monthLabel, monthMoney, paidSoFar, paymentsIn, todayKey } from "../../shared/money";
import { call, listen } from "../bridge";
import { byId, cloneTemplate, fillFields, flashToast, renderList } from "../dom";
import { isViewActive, onViewShown } from "../navigation";
import { loadShareBoard, onSharedChange, readSharedAll, sharedHead } from "../shares";
import { myId } from "../me";
import { drawBars, drawColumns, setGauge } from "../charts";
import { locale, t } from "../i18n";

// the budget: money in, bills and costs out, debts going down and goals filling up, with graphs for the month you're on

const KIND_LABEL: Record<BudgetKind, string> = { income: "in", bill: "bill", cost: "cost", debt: "debt", goal: "goal" };
const EMPTY: BudgetState = { items: [], comms: [], symbol: "€" };

let state: BudgetState = EMPTY;
let shared: Array<{ id: string; name: string; data: BudgetState }> = [];
let filter = "all";
let monthShift = 0;
let editingId = "";
// whose budget the form writes into, empty for my own
let formOwner = "";
// people who let me add to their budget
let addableOwners: Array<{ id: string; name: string }> = [];
let formKind: BudgetKind = "bill";
let formRepeat: BudgetRepeat = "monthly";

function shownMonth(): string {
  const now = new Date();
  return lastMonths(1, new Date(now.getFullYear(), now.getMonth() + monthShift, 1))[0];
}

function money(value: number, symbol = state.symbol): string {
  return formatMoney(value, symbol);
}

function describe(item: BudgetItem, symbol: string): string {
  const parts: string[] = [];
  if (item.kind === "debt") {
    if (item.amount) parts.push(t("{amount} a month", { amount: formatMoney(item.amount, symbol) }));
  } else if (item.kind === "goal") {
    if (item.by) parts.push(t("by {date}", { date: item.by }));
  } else {
    parts.push(formatMoney(item.amount, symbol));
    parts.push(item.repeat === "monthly" ? (item.until ? t("monthly, stopped {date}", { date: item.until }) : t("monthly from {date}", { date: item.date.slice(0, 7) })) : t("on {date}", { date: item.date }));
  }
  if (item.category) parts.push(item.category);
  if (item.addedByName) parts.push(item.addedBy === myId() ? t("added by you") : t("added by {name}", { name: item.addedByName }));
  return parts.join(" · ");
}

// debts fill as they're paid off, goals as they're saved up
function progressText(item: BudgetItem, symbol: string): string {
  const done = paidSoFar(item);
  const left = Math.max(0, item.total - done);
  const vars = { done: formatMoney(done, symbol), total: formatMoney(item.total, symbol), left: formatMoney(left, symbol) };
  if (item.kind === "debt") return left ? t("{done} paid of {total} · {left} left", vars) : t("paid off · {total}", vars);
  return left ? t("{done} saved of {total} · {left} to go", vars) : t("reached · {done}", vars);
}

// the calls for my own budget, or for someone's who lets me add to theirs (the server checks again)
function budgetCalls(owner: string) {
  const done = (next: BudgetState) => {
    if (!owner) return paint(next);
    shared = shared.map((person) => person.id === owner ? { ...person, data: next } : person);
    paintShared();
  };
  return {
    save: async (draft: BudgetDraft) => done(owner ? await call(CHANNELS.budgetSharedSave, owner, draft) : await call(CHANNELS.budgetSave, draft)),
    remove: async (id: string) => done(owner ? await call(CHANNELS.budgetSharedRemove, owner, id) : await call(CHANNELS.budgetRemove, id)),
    stop: async (id: string) => done(owner ? await call(CHANNELS.budgetSharedStop, owner, id) : await call(CHANNELS.budgetStop, id)),
    pay: async (id: string, amount: string | number, date: string) => done(owner ? await call(CHANNELS.budgetSharedPay, owner, id, amount, date) : await call(CHANNELS.budgetPay, id, amount, date))
  };
}

function oops(error: unknown): void {
  flashToast(error instanceof Error ? error.message.replace(/^.*Error: /, "") : t("couldn't do that"));
}

// owner: whose budget this card is in, when it's someone else's I may add to. readOnly: someone else's I only see
function budgetCard(item: BudgetItem, symbol: string, readOnly = false, owner = ""): HTMLElement {
  const row = fillFields(cloneTemplate("template-budget"), { kind: t(KIND_LABEL[item.kind]), name: item.name, meta: describe(item, symbol) });
  row.querySelector(".badge")!.classList.add(`kind-${item.kind}`);
  if (item.until) row.classList.add("stopped");
  const tracked = item.kind === "debt" || item.kind === "goal";
  if (tracked) {
    const done = paidSoFar(item);
    const bar = row.querySelector<HTMLElement>("[data-progress]")!;
    bar.hidden = false;
    bar.querySelector<HTMLElement>(".led-track > i")!.style.width = `calc(${Math.min(1, done / Math.max(item.total, 1)) * 100}% - 4px)`;
    bar.classList.toggle("full", done >= item.total);
    fillFields(row, { progress: progressText(item, symbol) });
  }
  // in someone else's budget I can log payments, and change only what I put in
  const mine = !owner || item.addedBy === myId();
  if (readOnly || !mine) {
    row.querySelector("[data-action='remove']")?.remove();
    row.querySelector("[data-action='stop']")?.remove();
  }
  if (readOnly) return row;
  const calls = budgetCalls(owner);
  row.querySelector("[data-action='remove']")?.addEventListener("click", async (event) => {
    event.stopPropagation();
    if (!confirm(t("remove {name}? its history goes too", { name: item.name }))) return;
    await calls.remove(item.id).catch(oops);
  });
  const stop = row.querySelector<HTMLElement>("[data-action='stop']");
  if (stop && item.repeat === "monthly" && !tracked) {
    stop.hidden = false;
    stop.textContent = item.until ? t("restart") : t("stop");
    stop.addEventListener("click", async (event) => {
      event.stopPropagation();
      await calls.stop(item.id).catch(oops);
    });
  }
  if (tracked) {
    const pay = row.querySelector<HTMLFormElement>("[data-pay]")!;
    const field = pay.querySelector<HTMLInputElement>("[data-pay-amount]")!;
    pay.hidden = false;
    pay.querySelector("[data-pay-label]")!.textContent = item.kind === "debt" ? t("paid") : t("saved");
    field.placeholder = item.amount ? String(item.amount) : item.kind === "debt" ? t("paid") : t("saved");
    pay.addEventListener("click", (event) => event.stopPropagation());
    pay.addEventListener("submit", async (event) => {
      event.preventDefault();
      const amount = field.value.trim() || (item.amount ? String(item.amount) : "");
      if (!amount) return field.focus();
      try {
        await calls.pay(item.id, amount, todayKey());
        flashToast(item.kind === "debt" ? t("payment logged") : t("saved toward it"));
      } catch (error) {
        oops(error);
      }
    });
    pay.querySelector("[data-action='undo']")!.addEventListener("click", async () => {
      if (!item.payments.length) return;
      try {
        await calls.pay(item.id, -1, "");
        flashToast(t("took back the last one"));
      } catch (error) {
        oops(error);
      }
    });
  }
  if (mine) row.addEventListener("click", () => startEdit(item, owner));
  return row;
}

// graphs and gauges for one budget, into a board laid out like #budget-board
function drawBoard(board: HTMLElement, data: BudgetState, month: string): void {
  const now = monthMoney(data, month);
  const left = now.in - now.out - now.saved;
  const symbol = data.symbol;
  const short = (value: number) => formatMoney(value, symbol, true);
  const gauge = (name: string) => board.querySelector<HTMLElement>(`[data-meter="${name}"]`)!;
  // in and out sit against each other, left against what came in
  const scale = Math.max(now.in, now.out, 1);
  setGauge(gauge("in"), short(now.in), now.in / scale);
  setGauge(gauge("out"), short(now.out), now.out / scale);
  setGauge(gauge("left"), short(left), now.in ? 0.5 + left / (now.in * 2) : left < 0 ? 0 : 0.5);
  gauge("left").classList.toggle("short", left < 0);

  const months = lastMonths(6, new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)) - 1, 1));
  const flows = months.map((key) => monthMoney(data, key));
  drawColumns(board.querySelector<HTMLElement>("[data-chart='flow']")!, {
    labels: months.map((key) => monthLabel(key, false, locale())),
    details: flows.map((flow) => {
      const left = t("{amount} left", { amount: formatMoney(flow.in - flow.out - flow.saved, symbol) });
      return flow.saved ? `${t("{amount} saved", { amount: formatMoney(flow.saved, symbol) })} · ${left}` : left;
    }),
    series: [
      { name: t("in"), color: "var(--chi)", values: flows.map((flow) => flow.in) },
      { name: t("out"), color: "var(--violet)", values: flows.map((flow) => flow.out) }
    ],
    format: (value) => formatMoney(value, symbol),
    axis: short,
    current: months.length - 1
  });

  const categories = fixedByCategory(data, month);
  drawBars(board.querySelector<HTMLElement>("[data-chart='category']")!, categories.map((row) => ({ label: row.category, value: row.amount })), (value) => formatMoney(value, symbol), t("no monthly bills yet"));

  const tracked = data.items.filter((item) => item.kind === "debt" || item.kind === "goal");
  const goalsWrap = board.querySelector<HTMLElement>("[data-chart-wrap='goals']")!;
  goalsWrap.hidden = !tracked.length;
  drawBars(board.querySelector<HTMLElement>("[data-chart='goals']")!, tracked.map((item) => {
    const done = paidSoFar(item);
    const pace = paymentsIn(item, month);
    return {
      label: `${item.kind === "debt" ? t("owe") : t("save")} · ${item.name}`,
      value: done,
      note: `${t("{percent}% of {total}", { percent: Math.round((done / Math.max(item.total, 1)) * 100), total: short(item.total) })}${pace ? ` · ${t("{amount} this month", { amount: short(pace) })}` : ""}`,
      color: item.kind === "debt" ? "var(--violet)" : "var(--green)"
    };
  }), (value) => formatMoney(value, symbol));
  // bars here are each against their own total, not each other
  board.querySelectorAll<HTMLElement>("[data-chart='goals'] .hbar").forEach((bar, at) => {
    const item = tracked[at];
    const fill = bar.querySelector<HTMLElement>(".hbar-track > i");
    if (fill && item) fill.style.width = `${Math.min(1, paidSoFar(item) / Math.max(item.total, 1)) * 100}%`;
  });
}

function sortItems(items: BudgetItem[]): BudgetItem[] {
  const order: BudgetKind[] = ["income", "bill", "cost", "debt", "goal"];
  return [...items].sort((a, b) => {
    if (Boolean(a.until) !== Boolean(b.until)) return a.until ? 1 : -1;
    if (a.kind !== b.kind) return order.indexOf(a.kind) - order.indexOf(b.kind);
    return b.amount + b.total - (a.amount + a.total);
  });
}

function paint(next?: BudgetState): void {
  if (next) state = next;
  const month = shownMonth();
  byId("budget-month").textContent = monthLabel(month, true, locale());
  drawBoard(byId("budget-board"), state, month);
  const items = sortItems(state.items).filter((item) => filter === "all" || item.kind === filter);
  renderList(byId("budget-list"), items, (item) => budgetCard(item, state.symbol));
  byId("budget-empty").hidden = items.length > 0;
  const categories = [...new Set(state.items.map((item) => item.category).filter(Boolean))];
  byId("budget-categories").replaceChildren(...categories.map((name) => Object.assign(document.createElement("option"), { value: name })));
  paintShared();
}

// someone who shares their budget with me: their month and their list, read only, under mine
function paintShared(): void {
  const box = byId("budget-shared");
  box.hidden = !shared.length;
  const month = shownMonth();
  box.replaceChildren(...shared.map((person) => {
    const section = document.createElement("div");
    section.className = "stack shared-box";
    const board = byId("budget-board").cloneNode(true) as HTMLElement;
    board.removeAttribute("id");
    drawBoard(board, person.data, month);
    const list = document.createElement("ul");
    list.className = "card-list";
    const canAdd = addableOwners.some((owner) => owner.id === person.id);
    renderList(list, sortItems(person.data.items), (item) => budgetCard(item, person.data.symbol, !canAdd, canAdd ? person.id : ""));
    section.append(sharedHead(person.name, "budget"), board, list);
    return section;
  }));
}

async function loadShared(): Promise<void> {
  shared = await readSharedAll<BudgetState>("budget", EMPTY);
  const board = await loadShareBoard();
  addableOwners = board.withMe.filter((person) => person.kinds.includes("budget-add")).map(({ id, name }) => ({ id, name }));
  if (formOwner && !addableOwners.some((owner) => owner.id === formOwner)) formOwner = "";
  paintTargets();
  paintShared();
}

// the "into" switch on the form, only there when someone lets me add to their budget
function paintTargets(): void {
  const box = byId("budget-target");
  box.hidden = !addableOwners.length;
  const options = [{ id: "", name: t("mine") }, ...addableOwners.map((owner) => ({ id: owner.id, name: t("{name}'s", { name: owner.name.toLowerCase() }) }))];
  box.replaceChildren(...options.map((option) => {
    const key = document.createElement("button");
    key.type = "button";
    key.className = "seg-option";
    key.classList.toggle("active", option.id === formOwner);
    key.disabled = Boolean(editingId) && option.id !== formOwner;
    key.textContent = option.name;
    key.addEventListener("click", () => {
      formOwner = option.id;
      paintTargets();
    });
    return key;
  }));
}

// the add form changes its fields with the kind of thing
function shapeForm(): void {
  byId("budget-kind").querySelectorAll<HTMLElement>("[data-kind]").forEach((item) => item.classList.toggle("active", item.dataset.kind === formKind));
  byId("budget-repeat").querySelectorAll<HTMLElement>("[data-repeat]").forEach((item) => item.classList.toggle("active", item.dataset.repeat === formRepeat));
  const tracked = formKind === "debt" || formKind === "goal";
  const total = byId<HTMLInputElement>("budget-total");
  const amount = byId<HTMLInputElement>("budget-amount");
  total.hidden = !tracked;
  total.placeholder = formKind === "debt" ? t("owed in total") : t("target");
  amount.hidden = formKind === "goal";
  amount.placeholder = formKind === "debt" ? t("a month (optional)") : formKind === "income" ? t("how much comes in") : t("how much");
  byId("budget-repeat").hidden = tracked;
  byId("budget-category").hidden = tracked;
  byId("budget-date-label").textContent = t(formKind === "goal" ? "by" : formKind === "debt" ? "since" : formRepeat === "monthly" ? "from" : "on");
  byId("budget-name").setAttribute("placeholder", t({ income: "where it comes from", bill: "rent, phone, adobe…", cost: "what you bought", debt: "who or what you owe", goal: "what you're saving for" }[formKind]));
}

function resetForm(): void {
  editingId = "";
  formOwner = "";
  paintTargets();
  byId<HTMLFormElement>("budget-form").reset();
  byId<HTMLInputElement>("budget-date").value = "";
  byId("budget-save").textContent = t("add");
  byId("budget-fold-title").textContent = t("add to the budget");
  byId("budget-cancel").hidden = true;
  shapeForm();
}

function startEdit(item: BudgetItem, owner = ""): void {
  editingId = item.id;
  formOwner = owner;
  paintTargets();
  formKind = item.kind;
  formRepeat = item.repeat;
  byId<HTMLInputElement>("budget-name").value = item.name;
  byId<HTMLInputElement>("budget-amount").value = item.amount ? String(item.amount) : "";
  byId<HTMLInputElement>("budget-total").value = item.total ? String(item.total) : "";
  byId<HTMLInputElement>("budget-date").value = item.kind === "goal" ? item.by : item.date;
  byId<HTMLInputElement>("budget-category").value = item.category;
  byId("budget-save").textContent = t("save");
  byId("budget-fold-title").textContent = t("edit {name}", { name: item.name });
  byId("budget-cancel").hidden = false;
  shapeForm();
  const fold = byId<HTMLDetailsElement>("budget-fold");
  fold.open = true;
  fold.scrollIntoView({ behavior: "smooth", block: "start" });
}

async function submit(): Promise<void> {
  const date = byId<HTMLInputElement>("budget-date").value;
  const draft: BudgetDraft = {
    id: editingId || undefined,
    kind: formKind,
    name: byId<HTMLInputElement>("budget-name").value,
    amount: formKind === "goal" ? 0 : byId<HTMLInputElement>("budget-amount").value,
    total: formKind === "debt" || formKind === "goal" ? byId<HTMLInputElement>("budget-total").value : 0,
    repeat: formKind === "debt" || formKind === "goal" ? "once" : formRepeat,
    date: formKind === "goal" ? "" : date,
    by: formKind === "goal" ? date : "",
    category: formKind === "debt" || formKind === "goal" ? "" : byId<HTMLInputElement>("budget-category").value
  };
  try {
    await budgetCalls(formOwner).save(draft);
    flashToast(editingId ? t("saved") : formOwner ? t("added to {name}'s budget", { name: addableOwners.find((owner) => owner.id === formOwner)?.name ?? "" }) : t("added"));
    resetForm();
    byId<HTMLDetailsElement>("budget-fold").open = false;
  } catch (error) {
    flashToast(error instanceof Error ? error.message.replace(/^.*Error: /, "") : t("couldn't save that"));
  }
}

export function setupBudgetView(): void {
  shapeForm();
  byId("budget-kind").addEventListener("click", (event) => {
    const option = (event.target as Element).closest<HTMLElement>("[data-kind]");
    if (!option) return;
    formKind = option.dataset.kind as BudgetKind;
    formRepeat = formKind === "bill" || formKind === "income" ? "monthly" : "once";
    shapeForm();
  });
  byId("budget-repeat").addEventListener("click", (event) => {
    const option = (event.target as Element).closest<HTMLElement>("[data-repeat]");
    if (!option) return;
    formRepeat = option.dataset.repeat === "once" ? "once" : "monthly";
    shapeForm();
  });
  byId("budget-form").addEventListener("submit", (event) => {
    event.preventDefault();
    void submit();
  });
  byId("budget-cancel").addEventListener("click", () => {
    resetForm();
    byId<HTMLDetailsElement>("budget-fold").open = false;
  });
  byId("budget-filter").addEventListener("click", (event) => {
    const chip = (event.target as Element).closest<HTMLElement>("[data-filter]");
    if (!chip) return;
    filter = chip.dataset.filter || "all";
    byId("budget-filter").querySelectorAll(".chip").forEach((item) => item.classList.toggle("active", item === chip));
    paint();
  });
  byId("budget-prev").addEventListener("click", () => {
    monthShift -= 1;
    paint();
  });
  byId("budget-next").addEventListener("click", () => {
    monthShift = Math.min(monthShift + 1, 12);
    paint();
  });
  listen<BudgetState>(CHANNELS.budgetUpdated, (next) => paint(next));
  // a commission marked paid is money in
  listen(CHANNELS.studioUpdated, async () => {
    if (isViewActive("budget")) paint(await call<BudgetState>(CHANNELS.budgetGet).catch(() => state));
  });
  onViewShown("budget", async () => {
    paint(await call<BudgetState>(CHANNELS.budgetGet).catch(() => state));
    void loadShared();
  });
  onSharedChange("budget", () => void loadShared());
  onSharedChange("comms", () => void loadShared());
}
